import { useRef, useState } from "react";
import { Camera, CheckCircle, RefreshCw } from "lucide-react";
import type { CandidateProfile } from "@applicant-reviewer/api";
import { useUploadMedia } from "../../../../hooks/mutations/useMediaMutations";
import { useUpdateProfile } from "../../../../hooks/mutations/useProfileMutations";

interface IDCaptureSectionProps {
  profile: CandidateProfile;
}

interface IDSide {
  side: "front" | "back";
  label: string;
  mediaIdField: "idFrontMediaId" | "idBackMediaId";
}

const ID_SIDES: IDSide[] = [
  { side: "front", label: "ID Front", mediaIdField: "idFrontMediaId" },
  { side: "back", label: "ID Back", mediaIdField: "idBackMediaId" },
];

interface UploadState {
  front: { mediaId: string | null; previewUrl: string | null; uploading: boolean; error: string | null };
  back: { mediaId: string | null; previewUrl: string | null; uploading: boolean; error: string | null };
}

export default function IDCaptureSection({ profile }: IDCaptureSectionProps) {
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);

  const [uploadState, setUploadState] = useState<UploadState>({
    front: {
      mediaId: profile.idFrontMediaId ?? null,
      previewUrl: null,
      uploading: false,
      error: null,
    },
    back: {
      mediaId: profile.idBackMediaId ?? null,
      previewUrl: null,
      uploading: false,
      error: null,
    },
  });

  const uploadMedia = useUploadMedia();
  const updateProfile = useUpdateProfile();

  const getInputRef = (side: "front" | "back") =>
    side === "front" ? frontInputRef : backInputRef;

  const handleFileSelect = async (side: "front" | "back", file: File) => {
    const previewUrl = URL.createObjectURL(file);

    setUploadState((prev) => ({
      ...prev,
      [side]: { ...prev[side], uploading: true, error: null, previewUrl },
    }));

    try {
      const result = await uploadMedia.mutateAsync({
        request: {
          type: side === "front" ? "id_front" : "id_back",
          contentType: file.type,
          fileSize: file.size,
          applicationId: "profile",
        },
        file,
      });

      const { mediaId } = result;
      const mediaIdField = side === "front" ? "idFrontMediaId" : "idBackMediaId";

      setUploadState((prev) => ({
        ...prev,
        [side]: { ...prev[side], mediaId, uploading: false },
      }));

      // Build the update payload, including idCapturedAt if both sides are now captured
      const otherSideMediaId =
        side === "front"
          ? uploadState.back.mediaId
          : uploadState.front.mediaId;

      const profileUpdate: Parameters<typeof updateProfile.mutate>[0] = {
        [mediaIdField]: mediaId,
      };

      if (otherSideMediaId) {
        profileUpdate.idCapturedAt = new Date().toISOString();
      }

      updateProfile.mutate(profileUpdate);
    } catch {
      setUploadState((prev) => ({
        ...prev,
        [side]: { ...prev[side], uploading: false, error: "Upload failed. Please try again." },
      }));
    }
  };

  const handleInputChange = (side: "front" | "back") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFileSelect(side, file);
    }
    // Reset input so the same file can be reselected
    e.target.value = "";
  };

  const hasCapture = (side: "front" | "back") =>
    uploadState[side].mediaId !== null || uploadState[side].previewUrl !== null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
      <div>
        <h2 className="text-base font-bold text-gray-900">ID Verification</h2>
        <p className="text-xs text-gray-500 mt-1">
          Upload a photo of the front and back of your government-issued ID.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {ID_SIDES.map(({ side, label }) => {
          const state = uploadState[side];
          const captured = hasCapture(side);
          const inputRef = getInputRef(side);

          return (
            <div key={side} className="flex flex-col items-center gap-2">
              <p className="text-xs font-semibold text-gray-700">{label}</p>

              {/* Upload zone */}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={state.uploading}
                className={`relative w-full aspect-[3/2] rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-colors overflow-hidden ${
                  captured
                    ? "border-[#0156FF] bg-blue-50"
                    : "border-gray-200 bg-gray-50 hover:border-[#0156FF] hover:bg-blue-50"
                } ${state.uploading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {state.uploading ? (
                  <div className="w-7 h-7 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin" />
                ) : state.previewUrl ? (
                  <>
                    <img
                      src={state.previewUrl}
                      alt={`${label} preview`}
                      className="absolute inset-0 w-full h-full object-cover rounded-xl"
                    />
                    <div className="absolute top-1 right-1 bg-[#0156FF] rounded-full p-0.5">
                      <CheckCircle className="w-3.5 h-3.5 text-white" />
                    </div>
                  </>
                ) : captured ? (
                  <>
                    <CheckCircle className="w-8 h-8 text-[#0156FF]" />
                    <p className="text-xs text-[#0156FF] font-semibold mt-1">Uploaded</p>
                  </>
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-gray-400" />
                    <p className="text-xs text-gray-500 mt-1">Tap to capture</p>
                  </>
                )}
              </button>

              {/* Replace button */}
              {captured && !state.uploading && (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#0156FF] transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Replace
                </button>
              )}

              {state.error && (
                <p className="text-xs text-red-600 text-center">{state.error}</p>
              )}

              {/* Hidden file input */}
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleInputChange(side)}
              />
            </div>
          );
        })}
      </div>

      {updateProfile.isError && (
        <p className="text-xs text-red-600">Failed to save ID info. Please try again.</p>
      )}

      {profile.idCapturedAt && (
        <p className="text-xs text-green-600 font-semibold text-center">
          ID verified on {new Date(profile.idCapturedAt).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}
